import { Component, OnInit } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { FormBuilder } from '@angular/forms';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '@src/app/shared/services/theme.service';
import { SessionService } from '@src/app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';
import { SettingsService, CompanyResponse, BrandingSettingsRequest, CompanySettingsResponse, CompanyEmailLogoUploadRequest } from '@src/app/shared/backend/clients';
import { DEFAULT_BRANDING_SETTINGS } from '@src/app/shared/constants/detault-theme.constants';

type ColourControlName =
  | 'primaryColour'
  | 'primaryAccentColour'
  | 'secondaryColour'
  | 'secondaryAccentColour'
  | 'primaryBtnColour'
  | 'secondaryBtnColour'
  | 'primaryBtnHoverColour'
  | 'secondaryBtnHoverColour'
  | 'primaryBtnTextColour'
  | 'secondaryBtnTextColour'
  | 'primaryBtnHoverTextColour'
  | 'secondaryBtnHoverTextColour'
  | 'backgroundColour'
  | 'headerBackgroundColour'
  | 'mainWrapperBackgroundColour'
  | 'tableHeaderBackgroundColour';

@Component({
    selector: 'app-theme-settings',
    templateUrl: './theme-settings.component.html',
    styleUrls: ['./theme-settings.component.scss'],
    host: {
        class: 'app-theme-settings'
    },
    standalone: false
})
export class ThemeSettingsComponent implements OnInit {
  brandingExists = false;
  uploadedLogoUrl: string | null = null;
  logoDataUrl: SafeUrl | null = null;
  companyLogoLoadFailed = false;
  file: File | null = null;
  fileUploadError = '';
  company: CompanyResponse | null = null;
  emailLogoPreviewUrl: string | null = null;
  emailLogoUploadError = '';

  public themeSettingsFormGroup = this.fb.group({
    logo: [''],
    logoUrl: [''],
    companyName: [''],
    primaryColour: [''],
    primaryColourHex: [''],
    primaryAccentColour: [''],
    primaryAccentColourHex: [''],
    primaryBtnTextColour: [''],
    primaryBtnTextColourHex: [''],
    primaryBtnColour: [''],
    primaryBtnColourHex: [''],
    primaryBtnHoverColour: [''],
    primaryBtnHoverColourHex: [''],
    primaryBtnHoverTextColour: [''],
    primaryBtnHoverTextColourHex: [''],
    secondaryColour: [''],
    secondaryColourHex: [''],
    secondaryAccentColour: [''],
    secondaryAccentColourHex: [''],
    secondaryBtnColour: [''],
    secondaryBtnColourHex: [''],
    secondaryBtnTextColour: [''],
    secondaryBtnTextColourHex: [''],
    secondaryBtnHoverColour: [''],
    secondaryBtnHoverColourHex: [''],
    secondaryBtnHoverTextColour: [''],
    secondaryBtnHoverTextColourHex: [''],
    backgroundColour: [''],
    backgroundColourHex: [''],
    headerBackgroundColour: [''],
    headerBackgroundColourHex: [''],
    mainWrapperBackgroundColour: [''],
    mainWrapperBackgroundColourHex: [''],
    mainWrapperPaddingTop: [''],
    mainWrapperPaddingRight: [''],
    mainWrapperPaddingBottom: [''],
    mainWrapperPaddingLeft: [''],
    tableHeaderBackgroundColour: [''],
    tableHeaderBackgroundColourHex: [''],
  });

  constructor(
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private themeService: ThemeService,
    private toasterService: ToasterService,
    private session: SessionService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.session.currentCompanyData$.subscribe(company => {
      this.company = company;
    });

    this.session.getCompanySettings().then((settings: CompanySettingsResponse) => {
      const branding = settings.brandingSettings;
      this.brandingExists = branding !== null;

      if (branding) {
        this.patchBrandingValues({
          primaryColour: branding.primaryColour,
          primaryAccentColour: branding.primaryAccentColour,
          secondaryColour: branding.secondaryColour,
          secondaryAccentColour: branding.secondaryAccentColour,
          primaryBtnColour: branding.primaryBtnColour,
          secondaryBtnColour: branding.secondaryBtnColour,
          primaryBtnHoverColour: branding.primaryBtnHoverColour,
          secondaryBtnHoverColour: branding.secondaryBtnHoverColour,
          primaryBtnTextColour: branding.primaryBtnTextColour,
          secondaryBtnTextColour: branding.secondaryBtnTextColour,
          primaryBtnHoverTextColour: branding.primaryBtnHoverTextColour,
          secondaryBtnHoverTextColour: branding.secondaryBtnHoverTextColour,
          backgroundColour: branding.backgroundColour,
          headerBackgroundColour: branding.headerBackgroundColour,
          mainWrapperBackgroundColour: branding.mainWrapperBackgroundColour,
          tableHeaderBackgroundColour: branding.tableHeaderBackgroundColour
        });

        this.themeSettingsFormGroup.patchValue({
          mainWrapperPaddingTop: branding.mainWrapperPaddingTop ?? null,
          mainWrapperPaddingRight: branding.mainWrapperPaddingRight ?? null,
          mainWrapperPaddingBottom: branding.mainWrapperPaddingBottom ?? null,
          mainWrapperPaddingLeft: branding.mainWrapperPaddingLeft ?? null
        });

        this.uploadedLogoUrl = branding.logoUrl ?? null;
        this.emailLogoPreviewUrl = branding.emailLogoBytesUrl ?? null;
      }
    });
  }

  private patchBrandingValues(
    values: Partial<Record<ColourControlName, string | null | undefined>>
  ) {
    this.themeSettingsFormGroup.patchValue({
      primaryColour: this.toPickerColour(values.primaryColour),
      primaryColourHex: this.toHexInput(values.primaryColour),

      primaryAccentColour: this.toPickerColour(values.primaryAccentColour),
      primaryAccentColourHex: this.toHexInput(values.primaryAccentColour),

      secondaryColour: this.toPickerColour(values.secondaryColour),
      secondaryColourHex: this.toHexInput(values.secondaryColour),

      secondaryAccentColour: this.toPickerColour(values.secondaryAccentColour),
      secondaryAccentColourHex: this.toHexInput(values.secondaryAccentColour),

      primaryBtnColour: this.toPickerColour(values.primaryBtnColour),
      primaryBtnColourHex: this.toHexInput(values.primaryBtnColour),

      secondaryBtnColour: this.toPickerColour(values.secondaryBtnColour),
      secondaryBtnColourHex: this.toHexInput(values.secondaryBtnColour),

      primaryBtnHoverColour: this.toPickerColour(values.primaryBtnHoverColour),
      primaryBtnHoverColourHex: this.toHexInput(values.primaryBtnHoverColour),

      secondaryBtnHoverColour: this.toPickerColour(values.secondaryBtnHoverColour),
      secondaryBtnHoverColourHex: this.toHexInput(values.secondaryBtnHoverColour),

      primaryBtnTextColour: this.toPickerColour(values.primaryBtnTextColour),
      primaryBtnTextColourHex: this.toHexInput(values.primaryBtnTextColour),

      secondaryBtnTextColour: this.toPickerColour(values.secondaryBtnTextColour),
      secondaryBtnTextColourHex: this.toHexInput(values.secondaryBtnTextColour),

      primaryBtnHoverTextColour: this.toPickerColour(values.primaryBtnHoverTextColour),
      primaryBtnHoverTextColourHex: this.toHexInput(values.primaryBtnHoverTextColour),

      secondaryBtnHoverTextColour: this.toPickerColour(values.secondaryBtnHoverTextColour),
      secondaryBtnHoverTextColourHex: this.toHexInput(values.secondaryBtnHoverTextColour),

      backgroundColour: this.toPickerColour(values.backgroundColour),
      backgroundColourHex: this.toHexInput(values.backgroundColour),

      headerBackgroundColour: this.toPickerColour(values.headerBackgroundColour),
      headerBackgroundColourHex: this.toHexInput(values.headerBackgroundColour),

      mainWrapperBackgroundColour: this.toPickerColour(values.mainWrapperBackgroundColour),
      mainWrapperBackgroundColourHex: this.toHexInput(values.mainWrapperBackgroundColour),

      tableHeaderBackgroundColour: this.toPickerColour(values.tableHeaderBackgroundColour),
      tableHeaderBackgroundColourHex: this.toHexInput(values.tableHeaderBackgroundColour)
    });
  }

  private toPickerColour(value: string | null | undefined): string {
    return (value || '').replace('#', '').slice(0, 6);
  }

  private toHexInput(value: string | null | undefined): string {
    if (!value) return '';
    const normalized = value.startsWith('#') ? value : `#${value}`;
    return normalized.slice(0, 7).toLowerCase();
  }

  private toApiColour(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    return this.toHexInput(value);
  }

  getHexValue(controlName: ColourControlName) {
    return this.themeSettingsFormGroup.controls[`${controlName}Hex`].value || null;
  }

  onNativeColourChange(controlName: ColourControlName, event: Event) {
    const value = (event.target as HTMLInputElement).value.toLowerCase();

    this.themeSettingsFormGroup.controls[controlName].setValue(this.toPickerColour(value));
    this.themeSettingsFormGroup.controls[`${controlName}Hex`].setValue(value);
  }

  onHexBlur(controlName: ColourControlName) {
    const colourControl = this.themeSettingsFormGroup.controls[controlName];
    const hexControl = this.themeSettingsFormGroup.controls[`${controlName}Hex`];

    const hexValue = (hexControl.value || '').trim();

    if (/^#([0-9A-F]{6})$/i.test(hexValue)) {
      colourControl.setValue(this.toPickerColour(hexValue));
      hexControl.setValue(hexValue.toLowerCase());
    } else {
      hexControl.setValue(this.toHexInput(colourControl.value));
    }
  }

  onLogoLoadError() {
    this.companyLogoLoadFailed = true;
  }

  async onCompanyLogoRemove() {
    try {
      await firstValueFrom(this.settingsService.apiSettingsBrandingLogoDelete());

      this.uploadedLogoUrl = null;
      this.file = null;
      this.companyLogoLoadFailed = false;
      this.themeSettingsFormGroup.get('logo')?.reset();
      this.themeSettingsFormGroup.get('logoUrl')?.reset();
      this.fileUploadError = '';

      this.toasterService.show(
        'success',
        this.translate.instant('Success'),
        this.translate.instant('Company logo removed successfully')
      );
    } catch (error) {
      console.error('Company logo removal failed:', error);
      this.toasterService.show(
        'error',
        this.translate.instant('Error'),
        this.translate.instant('Company logo removal failed')
      );
    }
  }

  showUploadPrompt() {
    this.uploadedLogoUrl = null;
    this.companyLogoLoadFailed = false;
    this.file = null;
    this.themeSettingsFormGroup.get('logo')?.reset();
    this.themeSettingsFormGroup.get('logoUrl')?.reset();
  }

  onCompanyLogoSelected(event: any) {
    this.onCompanyLogoUpload(event);
    this.companyLogoUploadFile();
  }

  onCompanyLogoUpload(event: any) {
    const uploadedFile = event.files[0];

    if (uploadedFile && !uploadedFile.type.startsWith('image/')) {
      this.fileUploadError = this.translate.instant('SYSTEM_SETTINGS.THEME.FILE_VALIDATION');
      this.themeSettingsFormGroup.get('logo')?.reset();
      this.themeSettingsFormGroup.get('logoUrl')?.reset();
      return;
    }

    if (uploadedFile.size > 1024 * 1024) {
      this.fileUploadError = this.translate.instant('SYSTEM_SETTINGS.THEME.FILE_SIZE_VALIDATION');
      this.themeSettingsFormGroup.get('file')?.reset();
      return;
    }

    this.file = uploadedFile;
    this.themeSettingsFormGroup.get('logo')?.setValue(uploadedFile);
    this.fileUploadError = '';
  }

  async companyLogoUploadFile(): Promise<string | null> {
    if (!this.file || !this.company) return null;

    try {
      const companyId = this.company?.id ? this.company.id : null;

      if (!companyId) {
        throw new Error('Company ID is missing');
      }

      const response = await firstValueFrom(
        this.settingsService.apiSettingsBrandingUploadLogoPut(this.file)
      );

      const logoUrl = response?.url || null;

      if (!logoUrl) {
        throw new Error('No logo URL returned');
      }

      this.uploadedLogoUrl = logoUrl;
      this.themeSettingsFormGroup.get('logoUrl')?.setValue(logoUrl);
      this.companyLogoLoadFailed = false;
      this.session.setBrandingLogoUrl(logoUrl);

      return logoUrl;
    } catch (error) {
      console.error('File upload failed', error);
      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.THEME.ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.FILE_UPLOAD_FAILED')
      );
      return null;
    }
  }

  async removeEmailLogo() {
    try {
      await firstValueFrom(this.settingsService.apiSettingsBrandingEmailLogoDelete());

      this.emailLogoPreviewUrl = null;
      this.emailLogoUploadError = '';

      this.toasterService.show(
        'success',
        this.translate.instant('Success'),
        this.translate.instant('Email logo removed successfully')
      );
    } catch (error) {
      console.error('Email logo removal failed:', error);
      this.toasterService.show(
        'error',
        this.translate.instant('Error'),
        this.translate.instant('Email logo removal failed')
      );
    }
  }

  async onEmailLogoSelected(event: any) {
    const file: File | undefined = event?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/svg+xml', 'image/png', 'image/jpeg'];
    const maxSizeBytes = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.emailLogoUploadError = 'Only SVG, PNG or JPEG files are allowed.';
      return;
    }

    if (file.size > maxSizeBytes) {
      this.emailLogoUploadError = 'File size must be less than 10MB.';
      return;
    }

    this.emailLogoUploadError = '';

    try {
      const pngBlob = await this.toPngBlob(file);
      this.emailLogoPreviewUrl = URL.createObjectURL(pngBlob);

      const buffer = await pngBlob.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      const payload: CompanyEmailLogoUploadRequest = {
        emailLogo: JSON.stringify(Array.from(bytes))
      };

      const response = await firstValueFrom(
        this.settingsService.apiSettingsBrandingUploadEmailLogoPut(payload)
      );

      const uploadedUrl = response?.url || null;
      if (uploadedUrl) {
        this.emailLogoPreviewUrl = uploadedUrl;
      }

      this.toasterService.show(
        'success',
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_MAIL_TEMPLATE_LOGO_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_MAIL_TEMPLATE_LOGO_UPLOADED_MSG'),
      );
    } catch (error) {
      console.error('Upload email logo failed', error);

      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_MAIL_TEMPLATE_LOGO_FAILED_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_MAIL_TEMPLATE_LOGO_UPLOADED_FAILED_MSG'),
      );
    }
  }

  private async toPngBlob(file: File): Promise<Blob> {
    if (file.type === 'image/png') {
      return file;
    }

    const imageUrl = URL.createObjectURL(file);

    try {
      const img = new Image();
      img.src = imageUrl;

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image load failed'));
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 512;
      canvas.height = img.naturalHeight || 512;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('PNG conversion failed'));
            return;
          }
          resolve(blob);
        }, 'image/png');
      });

      return pngBlob;
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }

  async createThemeSettings() {
    if (this.themeSettingsFormGroup.invalid) return;

    const branding: BrandingSettingsRequest = {
      primaryColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryColour.value),
      primaryAccentColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryAccentColour.value),
      secondaryColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryColour.value),
      secondaryAccentColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryAccentColour.value),
      primaryBtnColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnColour.value),
      secondaryBtnColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnColour.value),
      primaryBtnHoverColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnHoverColour.value),
      secondaryBtnHoverColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnHoverColour.value),
      primaryBtnTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnTextColour.value),
      secondaryBtnTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnTextColour.value),
      primaryBtnHoverTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnHoverTextColour.value),
      secondaryBtnHoverTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnHoverTextColour.value),
      backgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.backgroundColour.value),
      headerBackgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.headerBackgroundColour.value),
      mainWrapperBackgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.mainWrapperBackgroundColour.value),
      mainWrapperPaddingTop:this.themeSettingsFormGroup.controls.mainWrapperPaddingTop.value ?? undefined,
      mainWrapperPaddingRight:this.themeSettingsFormGroup.controls.mainWrapperPaddingRight.value ?? undefined,
      mainWrapperPaddingBottom:this.themeSettingsFormGroup.controls.mainWrapperPaddingBottom.value ?? undefined,
      mainWrapperPaddingLeft:this.themeSettingsFormGroup.controls.mainWrapperPaddingLeft.value ?? undefined,
      tableHeaderBackgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.tableHeaderBackgroundColour.value)
    };

    try {
      await firstValueFrom(this.settingsService.apiSettingsBrandingCreatePost(branding));
      this.brandingExists = true;

      this.toasterService.show(
        'success',
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_CREATED_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_CREATED_MSG')
      );

      await this.session.getCompanySettings(true);

    } catch (error) {
      console.error('Create theme settings failed:', error);
      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.THEME.ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.ERROR_GENERIC')
      );
    }
  }

  async updateThemeSettings() {
    if (this.themeSettingsFormGroup.invalid) return;

    const branding: BrandingSettingsRequest = {
      primaryColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryColour.value),
      primaryAccentColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryAccentColour.value),
      primaryBtnColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnColour.value),
      primaryBtnTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnTextColour.value),
      primaryBtnHoverColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnHoverColour.value),
      primaryBtnHoverTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.primaryBtnHoverTextColour.value),
      secondaryColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryColour.value),
      secondaryAccentColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryAccentColour.value),
      secondaryBtnColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnColour.value),
      secondaryBtnTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnTextColour.value),
      secondaryBtnHoverColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnHoverColour.value),
      secondaryBtnHoverTextColour: this.toApiColour(this.themeSettingsFormGroup.controls.secondaryBtnHoverTextColour.value),
      backgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.backgroundColour.value),
      headerBackgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.headerBackgroundColour.value),
      mainWrapperBackgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.mainWrapperBackgroundColour.value),
      mainWrapperPaddingTop: this.themeSettingsFormGroup.controls.mainWrapperPaddingTop.value ?? undefined,
      mainWrapperPaddingRight: this.themeSettingsFormGroup.controls.mainWrapperPaddingRight.value ?? undefined,
      mainWrapperPaddingBottom: this.themeSettingsFormGroup.controls.mainWrapperPaddingBottom.value ?? undefined,
      mainWrapperPaddingLeft: this.themeSettingsFormGroup.controls.mainWrapperPaddingLeft.value ?? undefined,
      tableHeaderBackgroundColour: this.toApiColour(this.themeSettingsFormGroup.controls.tableHeaderBackgroundColour.value)
    };

    console.log('Form value:', this.themeSettingsFormGroup.getRawValue());
    console.log('Branding payload:', branding);
    console.log('Serialized payload:', JSON.stringify(branding));

    try {
      await firstValueFrom(this.settingsService.apiSettingsBrandingUpdatePut(branding));

      this.toasterService.show(
        'success',
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_UPDATED_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.THEME_UPDATED_MSG')
      );

      await this.session.getCompanySettings(true);

    } catch (error) {
      console.error('Theme update failed:', error);
      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.THEME.ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.ERROR_GENERIC')
      );
    }
  }

  restoreThemeToFactorySettings() {
    const fallback = DEFAULT_BRANDING_SETTINGS;

    this.patchBrandingValues({
      primaryColour: fallback.primaryColour,
      primaryAccentColour: fallback.primaryAccentColour,
      secondaryColour: fallback.secondaryColour,
      secondaryAccentColour: fallback.secondaryAccentColour,
      primaryBtnColour: fallback.primaryBtnColour,
      secondaryBtnColour: fallback.secondaryBtnColour,
      primaryBtnHoverColour: fallback.primaryBtnHoverColour,
      secondaryBtnHoverColour: fallback.secondaryBtnHoverColour,
      primaryBtnTextColour: fallback.primaryBtnTextColour,
      secondaryBtnTextColour: fallback.secondaryBtnTextColour,
      primaryBtnHoverTextColour: fallback.primaryBtnHoverTextColour,
      secondaryBtnHoverTextColour: fallback.secondaryBtnHoverTextColour,
      backgroundColour: fallback.backgroundColour,
      headerBackgroundColour: fallback.headerBackgroundColour,
      mainWrapperBackgroundColour: fallback.mainWrapperBackgroundColour,
      tableHeaderBackgroundColour : fallback.tableHeaderBackgroundColour
    });

    this.themeService.loadTheme(fallback, this.company?.name);

    this.toasterService.show(
      'success',
      this.translate.instant('SYSTEM_SETTINGS.THEME.RESTORED_DEFAULTS_TITLE'),
      this.translate.instant('SYSTEM_SETTINGS.THEME.RESTORED_DEFAULTS_MSG')
    );
  }

  private sanitizeBranding(input: BrandingSettingsRequest): BrandingSettingsRequest {
    const result: BrandingSettingsRequest = {};

    for (const key in input) {
      const value = input[key as keyof BrandingSettingsRequest];
      if (value !== null && value !== undefined) {
        result[key as keyof BrandingSettingsRequest] = value;
      }
    }

    return result;
  }
}
